import { isEmpty } from 'lodash';
import { createSelector } from 'reselect';
import { getUserIdSelector } from '../user/selectors';

const DEFAULT_ARRAY = [];
const DEFAULT_OBJECT = {};

const getState = (state) => state.generalReducer;

export const getOpenedTaskId = createSelector(
  getState,
  (store) => store?.openedTaskId,
);

export const getTaskTmpProperties = createSelector(
  getState,
  (store) => store?.taskTmpProperties,
);

const getUserTasks = createSelector(getState, (store) => {
  const result = store?.userTasks;
  return isEmpty(result) ? DEFAULT_OBJECT : result;
});

export const getUserTasksIdList = createSelector(getUserTasks, (userTasks) => {
  const result = userTasks?.idList || [];
  return isEmpty(result) ? DEFAULT_ARRAY : result;
});

export const getUserTasksPage = createSelector(
  getUserTasks,
  (userTasks) => userTasks?.page,
);

export const getUserTasksCount = createSelector(
  getUserTasks,
  (userTasks) => userTasks?.count,
);

export const getUserTasksHasMore = createSelector(
  getUserTasks,
  (userTasks) => userTasks?.hasMore,
);

export const getProjectTasksStore = createSelector(getState, (store) => {
  const result = store?.projectTasks;
  return isEmpty(result) ? DEFAULT_OBJECT : result;
});

export const getProjectTasksFilters = createSelector(
  getProjectTasksStore,
  (projectTasks) => {
    const result = projectTasks?.filters;
    return isEmpty(result) ? DEFAULT_OBJECT : result;
  },
);

export const getProjectTasksSorts = createSelector(
  getProjectTasksStore,
  (projectTasks) => {
    const result = projectTasks?.sorts;
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  },
);

export const isSortingApplied = createSelector(
  getProjectTasksSorts,
  sorts => !isEmpty(sorts)
);

export const getDashboardTasksStore = createSelector(getState, (store) => {
  const result = store?.dashboardTasks;
  return isEmpty(result) ? DEFAULT_OBJECT : result;
});

export const getCompletedDashboardTasksCount = createSelector(
  getDashboardTasksStore,
  (dashboardTasks) => dashboardTasks?.completedTasks,
);

export const getTotalDashboardTasksCount = createSelector(
  getDashboardTasksStore,
  (dashboardTasks) => dashboardTasks?.totalTasks,
);

export const getUnassignedDashboardTasksCount = createSelector(
  getDashboardTasksStore,
  (dashboardTasks) => dashboardTasks?.unassignedTasks,
);

const getProjectIdProp = (state, props) => props?.projectId;

export const getTasksIdListByProjectId = createSelector(
  [getState, getProjectIdProp],
  (store, projectId) => {
    const result = store?.tasksByProjectId?.[projectId];
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  },
);

// FIXME: Need to refactor CalendarComponent so that the root component of Calendar would receive only the list of Ids instead of full tasks ojects
export const getCalendarTaskList = createSelector(
  [getTasksIdListByProjectId, (state) => state?.tasks?.entities],
  (idList, tasks) => idList?.map((taskId) => tasks?.[taskId]),
);

export const getUserTasksFilters = createSelector(getUserTasks, (userTasks) => {
  const result = userTasks?.filters;
  return isEmpty(result) ? DEFAULT_OBJECT : result;
});

export const getTasksCustomFiltersSelector = createSelector(
  getState,
  (generalState) => {
    const result = generalState?.tasksCustomFilters;
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  },
);

const getWorkspaceState = (state) => state?.workspaceReducer;

const getCurrentWorkspaceSelector = createSelector(
  getWorkspaceState,
  (workspaceReducer) => {
    const result = workspaceReducer?.currentWorkspace;
    return isEmpty(result) ? DEFAULT_OBJECT : result;
  },
);

export const getWorkspaceMembersSelector = createSelector(
  getCurrentWorkspaceSelector,
  (currentWorkspace) => {
    const result = currentWorkspace?.members;
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  },
);

export const getWorkspaceMembersSelectOptions = createSelector(
  getWorkspaceMembersSelector,
  (members) =>
    members?.map((user) => {
      return {
        value: user._id,
        label: user.fullName,
        email: user.email,
        avi: user.avi,
      };
    }),
);

const getUserReducer = (state) => state?.userReducer;

export const getUserReducerUsersSelector = createSelector(
  getUserReducer,
  (userReducer) => {
    const result = userReducer?.users;
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  },
);

export const hasUserReducerUsersSelector = createSelector(
  getUserReducerUsersSelector,
  (users) => users.length > 0,
);

export const getUserReducerUsersSelectOptions = createSelector(
  getUserReducerUsersSelector,
  (users) =>
    users?.map((user) => {
      return {
        value: user._id,
        label: user.fullName,
        email: user.email,
        avi: user.avi,
      };
    }),
);

const getSectionIdProp = (state, props) => props?.sectionId;

export const isInVisibleSectionIdList = createSelector(
  [getState, getSectionIdProp],
  (state, sectionId) =>
    (state?.visibleSectionIdList || DEFAULT_ARRAY).includes(sectionId),
);

export const getCurrentWorkspaceCreatedBy = createSelector(
  getWorkspaceMembersSelector,
  (currentWorkspace) => currentWorkspace?.createdBy
);

export const isCurrentWorkspaceOwnerOrAdmin = createSelector(
  [getWorkspaceMembersSelector, getUserIdSelector],
  (members, userId) => {
    const workspaceMember = members?.find(member => member?._id === userId);
    return ['admin', 'owner']?.includes(workspaceMember?.role);
  }
);
